import styled from "@emotion/styled";
import { m, PanInfo, useAnimation } from "framer-motion";
import { size } from "polished";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { getModuleThemeColors } from "@brexhq/next/src/components/pageBuilder/ModuleLayout";
import {
  isDarkTheme,
  type ModuleThemeType,
} from "@brexhq/shared/types/ModuleTheme";

import { mapToBounds } from "../helpers";

export interface ProgressSliderProps {
  scrollIndex: number;
  onSliderDrag: (percentDragged: number) => void;
  onDragEnd: (percentDragged: number) => void;
  onDragStart: () => void;
  moduleTheme: ModuleThemeType;
  scrollingContainer?: React.RefObject<HTMLDivElement>;
  maxRange: number;
}

const SliderWrapper = styled.div<{ moduleTheme: ModuleThemeType }>(
  ({ theme, moduleTheme }) => {
    const getBGColor = (moduleTheme: ModuleThemeType) => {
      if (moduleTheme === "PrimaryGray") {
        return theme.colors.border.hover;
      }
      if (isDarkTheme(moduleTheme)) {
        return theme.colors.border.inverse.pressed;
      }
      return theme.colors.background.secondary;
    };
    return {
      ...size(28, "100%"),
      position: "relative",
      cursor: "pointer",
      "&:before": {
        ...size(8, "100%"),
        content: "''",
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        background: getBGColor(moduleTheme),
      },
    };
  },
);

const SliderBar = styled(m.div, {
  shouldForwardProp: (prop) => prop !== "moduleTheme" && prop !== "sliderWidth",
})<{
  moduleTheme: ModuleThemeType;
  sliderWidth: number;
}>(({ moduleTheme, sliderWidth }) => {
  const { standard } = getModuleThemeColors(moduleTheme);
  return {
    touchAction: "none",
    width: `${sliderWidth}%`,
    height: "100%",
    cursor: "pointer",
    position: "relative",
    "&:before": {
      ...size(8, "100%"),
      content: "''",
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      background: standard.textPrimary,
    },
  };
});

/**
 * Progress Slider acts as a scroll bar that can be placed in offset locations (such as use in carousel)
 * mainly used for Firefox, use webkit styling for most applications.
 *
 * @param scrollIndex the index that you have scrolled to. This is a float point so you can do partial scrolls
 * @param onSliderDrag the function called as you drag "scroll bar"
 * @param onDragEnd function called when you finish dragging "scroll bar"
 * @param onDragStart function called when you start dragging the "scroll bar"
 * @param moduleTheme theme for slider
 * @param maxRange the largest value that scrollIndex can reach. This allows you to scroll from 0 to max range using scrollindex for current position
 * @returns component that looks like a scroll bar
 */
export const ProgressSlider: React.FC<ProgressSliderProps> = ({
  scrollIndex,
  onSliderDrag,
  onDragEnd,
  onDragStart,
  moduleTheme,
  maxRange,
}) => {
  const progressContainer = React.useRef<HTMLDivElement>();
  const progressSlider = React.useRef<HTMLDivElement>();
  const [currentOffset, setCurrentOffset] = useState(0);
  const slideBarLength = useMemo(() => (1 / (maxRange + 1)) * 100, [maxRange]);

  const measuredContainerRef = useCallback((node: HTMLDivElement) => {
    if (node !== null) {
      progressContainer.current = node;
    }
  }, []);
  const measuredSliderRef = useCallback((node: HTMLDivElement) => {
    if (node !== null) {
      progressSlider.current = node;
    }
  }, []);

  const getSliderWidth = useCallback(() => {
    return progressSlider?.current?.getBoundingClientRect().width ?? 0;
  }, [progressSlider]);

  const getContainerWidth = useCallback(() => {
    return progressContainer?.current?.getBoundingClientRect().width ?? 0;
  }, [progressContainer]);

  const controls = useAnimation();

  useEffect(() => {
    const nextX = scrollIndex * getSliderWidth();
    controls.set({ x: nextX });
    setCurrentOffset(nextX);
  }, [scrollIndex, getSliderWidth, controls]);

  // start slider events section
  const handlePanStart = () => {
    onDragStart();
  };

  const handlePan = (_: any, info: PanInfo) => {
    const newPosition = currentOffset + info.offset.x;
    const xPos = mapToBounds(
      newPosition,
      getContainerWidth(),
      getSliderWidth(),
    );
    if (info.delta.x !== 0) {
      controls.set({ x: xPos });
    }
    onSliderDrag(xPos / getSliderWidth());
  };

  const handlePanEnd = (_: any, info: PanInfo) => {
    const newPosition = currentOffset + info.offset.x;
    const xPos = mapToBounds(
      newPosition,
      getContainerWidth(),
      getSliderWidth(),
    );
    setCurrentOffset(xPos);
    onDragEnd(xPos / getSliderWidth());
  };

  const handleClick: React.MouseEventHandler<HTMLDivElement> = (event) => {
    const containerPosition =
      progressContainer.current?.getBoundingClientRect().left ?? 0;
    const delta = event.clientX - containerPosition;
    const xPos = Math.floor(delta / getSliderWidth());
    onDragEnd(xPos);
    setCurrentOffset(xPos * getSliderWidth());
  };

  // end slider event section

  return (
    <SliderWrapper
      ref={measuredContainerRef}
      moduleTheme={moduleTheme}
      onClick={handleClick}
    >
      <SliderBar
        moduleTheme={moduleTheme}
        sliderWidth={slideBarLength}
        ref={measuredSliderRef}
        onPanSessionStart={handlePanStart}
        onPan={handlePan}
        whileTap={{ cursor: "grabbing" }}
        onPanEnd={handlePanEnd}
        animate={controls}
        transition={{ bounce: 0, duration: 0.3, stiffness: 50 }}
      />
    </SliderWrapper>
  );
};
